import Modal from 'cccisd-modal';
import React, { useRef } from 'react';
import { Formik, Form, Field, CccisdInput, CccisdDatepicker, CccisdToggle } from 'cccisd-formik';
import axios from 'cccisd-axios';
import PlusIcon from 'cccisd-icons/plus2';
import PencilIcon from 'cccisd-icons/pencil5';
import CopyIcon from 'cccisd-icons/copy';
import moment from 'moment';
import style from './style.css';
import notification from 'cccisd-notification';
import _last from 'lodash/last';
import Tooltip from 'cccisd-tooltip';

const Boilerplate = window.cccisd.boilerplate;

const ProfDevActions = props => {
    const modal = useRef();

    const unflatten = data => {
        const result = {};
        for (const path of Object.keys(data)) {
            const keys = path.split('.');
            let current = result;

            for (let index = 0; index < keys.length - 1; index++) {
                const key = keys[index];

                if (!current[key]) {
                    if (Number.isNaN(Number(keys[index + 1]))) {
                        current[key] = {};
                    } else {
                        current[key] = [];
                    }
                }

                current = current[key];
            }

            current[keys[keys.length - 1]] = data[path];
        }
        return result;
    };

    const row = unflatten(props.row);
    const surveyList = row?.assignment?.surveyList;
    const assignmentId = row?.assignmentId;
    const isOpen = row?.deployment?.isOpen;
    const closed = row?.deployment?.closed;
    const currentDeployment = row?.deployment;
    const applyTimezoneOffset = date => {
        // Our server uses UTC time so we need to offset the start/end date entered by client
        return moment(date).utc().format('YYYY-MM-DD HH:mm:ss');
    };

    const onSubmit = async values => {
        const allSurveys = surveyList?.map(item => item.handle);

        const options = [];

        allSurveys.forEach(item => {
            if (values[item]) {
                options.push(item);
            }
        });

        if (!currentDeployment.hash) {
            // create
            try {
                const params = {
                    assignmentId,
                    description: null,
                    hash: Math.random().toString(36).substring(2, 9),
                    label: values.deploymentName,
                    roleHandle: 'respondent',
                    opensAt: applyTimezoneOffset(values.openDate),
                    closesAt: applyTimezoneOffset(values.closeDate),
                    settings: {
                        // add surveyList
                        options,
                        assignmentProgressId: props.row.assignmentProgressId,
                        recaptcha: false,
                        autoLogout: true,
                        isRepeated: false,
                        allowRetake: false,
                        requirePass: false,
                        useEndpoint: false,
                        closedRoster: false,
                        requireLogin: false,
                        closeOnTarget: false,
                        closedMessage: 'This survey is no longer accepting responses.',
                        completionUrl: null,
                        passcodeLabel: 'Your Passcode',
                        autoLogoutTime: 20,
                        repeatInterval: null,
                        completedMessage: 'You have already completed this survey. Thank you!',
                        respondentConfig: 'anonymous',
                        completionMessage: 'You have completed the survey! Thank you for your response!',
                        otherAllowedRoles: ['uberadmin', 'questprojectadmin'],
                        chainEmailFromPawn: null,
                        chainPhoneFromPawn: null,
                        individualPasscode: false,
                        logoutOnCompletion: false,
                        passcodeBackground: 'solidColor',
                        postCompletionAction: 'message',
                        anonAutoLogoutMessage:
                            '<p>Inactivity Timeout</p><p>We closed your survey session after a period of inactivity.</p>\n                    <p>Sorry! <a href="https://pcanc.test/d/9frmzn1" target="_blank" rel="noreferrer noopener">Start a new session</a></p>',
                        knownAutoLogoutMessage:
                            '<p>Inactivity Timeout</p><p>We closed your survey session after a period of inactivity. Sorry!</p>\n                        <p>You can resume your session from the link you were given, or contact your survey administrator for further assistance.</p>',
                        passcodeBackgroundColor: '#F1F0F5',
                        branchedCompletionConfig: [],
                        isAssignmentPlanEligible: false,
                        passcodeCompletionButtonText: 'Ok',
                        showReviewButtonOnCompletion: false,
                        metricToRespondentPropertyMappings: [],
                    },
                    timepoint: '',
                    type: 'anon',
                };

                const response = await axios.post(Boilerplate.route('api.assignmentDeployment.store'), params);

                if (response?.data?.status === 'success') {
                    closeModal();
                    props.table.current.loadData();
                    notification({ message: 'Evaluation successfully created', type: 'success' });
                } else {
                    closeModal();

                    if (response?.data?.errors?.closesOn) {
                        notification({
                            message: 'Close Date must be after Open Date',
                            type: 'danger',
                        });
                    }
                    if (response?.data?.errors?.opensAt) {
                        notification({
                            message: 'Open Date must be in the future',
                            type: 'danger',
                        });
                    }
                    if (response?.data?.errors?.group) {
                        notification({
                            message: 'You do not have permission to perform this action',
                            type: 'danger',
                        });
                    }
                }
            } catch (e) {
                console.error(e);
            }
        } else {
            try {
                // edit
                const params = {
                    ...currentDeployment,
                    label: values.deploymentName,
                    opensAt: applyTimezoneOffset(values.openDate),
                    closesAt: applyTimezoneOffset(values.closeDate),
                    settings: {
                        ...currentDeployment.settings,
                        options,
                        assignmentProgressId: props.row.assignmentProgressId,
                    },
                };

                const response = await axios.put(
                    Boilerplate.route('api.assignmentDeployment.update', {
                        deploymentId: currentDeployment.deploymentId,
                    }),
                    params
                );

                if (response?.data?.status === 'success') {
                    closeModal();
                    props.table.current.loadData();
                    notification({ message: 'Evaluation successfully edited', type: 'success' });
                } else {
                    closeModal();
                    if (response?.data?.errors?.closesOn) {
                        notification({
                            message: 'Close Date must be after Open Date',
                            type: 'danger',
                        });
                    }
                    if (response?.data?.errors?.opensAt) {
                        notification({
                            message: 'Open Date must be in the future',
                            type: 'danger',
                        });
                    }
                    notification({ message: response?.data?.errors?.group[0], type: 'danger' });
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const closeModal = () => {
        modal.current.close();
    };

    const validate = values => {
        try {
            let errors = {};
            if (!values.deploymentName) {
                errors.deploymentName = 'Evaluation Name is required';
            }

            if (!values.openDate) {
                errors.openDate = 'Open Date is required';
            }

            if (!values.closeDate) {
                errors.closeDate = 'Close Date is required';
            }

            if (surveyList && surveyList.length > 0) {
                const selectedSurveys = [];

                surveyList.forEach(survey => {
                    if (values[survey.handle]) {
                        selectedSurveys.push(survey.handle);
                    }
                });

                if (selectedSurveys.length === 0) {
                    const last = _last(surveyList).handle;
                    errors[last] = 'You must select at least one survey';
                }
            }
            return errors;
        } catch (e) {
            console.error(e);
        }
    };

    const getInitialValues = () => {
        if (!currentDeployment.hash) {
            return {};
        }
        const initialValues = {};

        currentDeployment.settings.options.forEach(item => {
            initialValues[item] = true;
        });

        initialValues.deploymentName = currentDeployment.label;
        initialValues.openDate = currentDeployment.opensAt;
        initialValues.closeDate = currentDeployment.closesAt;

        return initialValues;
    };

    return (
        <>
            {surveyList && assignmentId && (
                <Modal
                    ref={modal}
                    trigger={
                        <Tooltip
                            title={!currentDeployment?.hash ? 'Create Evaluation Survey' : 'Edit Evaluation Survey'}
                        >
                            <button
                                type="button"
                                className={!currentDeployment?.hash ? 'btn btn-secondary' : 'btn btn-warning'}
                                disabled={closed && currentDeployment?.hash}
                                style={{ marginRight: '1em' }}
                            >
                                {!currentDeployment?.hash ? (
                                    <>
                                        <PlusIcon />
                                    </>
                                ) : (
                                    <>
                                        <PencilIcon />
                                    </>
                                )}
                            </button>
                        </Tooltip>
                    }
                    title="Add Evaluation"
                    size="medium"
                    afterClose={() => {
                        props.loadData();
                    }}
                >
                    <Formik
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={!currentDeployment.hash ? {} : getInitialValues()}
                        render={() => (
                            <Form>
                                <div className={style.formSection}>
                                    <label className={`form-label ${style.required}`}>Evaluation Name:</label>
                                    <Field name="deploymentName" component={CccisdInput} />
                                </div>
                                <div className={style.dates}>
                                    {!isOpen && (
                                        <div>
                                            <label className={`form-label ${style.required}`}>Open Date:</label>

                                            <Field name="openDate" component={CccisdDatepicker} showTimeSelect />
                                        </div>
                                    )}
                                    <div>
                                        <label className={`form-label ${style.required}`}>Close Date:</label>
                                        <Field name="closeDate" component={CccisdDatepicker} showTimeSelect />
                                    </div>
                                </div>
                                <div className={style.formSection}>
                                    <label className={`form-label ${style.required}`}>Survey Selection:</label>
                                    {surveyList &&
                                        surveyList.length > 0 &&
                                        surveyList.map(item => {
                                            return (
                                                <Field name={item.handle} component={CccisdToggle} label={item.label} />
                                            );
                                        })}
                                </div>
                                <div className={style.dates}>
                                    <button
                                        className="btn btn-secondary"
                                        type="button"
                                        onClick={() => {
                                            closeModal();
                                        }}
                                    >
                                        Back
                                    </button>
                                    <button className="btn btn-primary" type="submit">
                                        Submit
                                    </button>
                                </div>
                            </Form>
                        )}
                    />
                </Modal>
            )}
            {currentDeployment?.hash && (
                <Tooltip title="Copy Evaluation Survey">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            navigator.clipboard.writeText(Boilerplate.url(`/d/${currentDeployment.hash}`));
                            notification({ message: 'Copied to clipboard', type: 'success' });
                        }}
                        disabled={closed}
                    >
                        <CopyIcon />
                    </button>
                </Tooltip>
            )}
        </>
    );
};

export default ProfDevActions;
